import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  fetchAdresseSuggestions, fetchSuggestionIngredients, useAutoResponsables,
  useCommerces, useCreateMenuPerso, useDeleteMenuPerso, useEchangerMenus,
  useItineraire, useMenus, usePersonnes, useReglagesMenus,
  useSetMenuSelection, useSetPrefsJour, useSetPrefsRepas, useSetReglagesMenus,
  useSetResponsables, useSetResponsablesJour, useToggleFavoriCommerce,
} from '../api/hooks'
import type {
  AdresseSuggestion, Commerce, MenuCatalogueItem, MenuJour, MenuPersoOut,
  MenuRepasJour, MenuResponsable, MenuSuggestion, PrefDisponible,
} from '../api/types'
import Avatar from '../components/Avatar'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

const fmtDate = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
}

const TAG_LABEL: Record<string, string> = {
  local: '🌶️ Local',
  bbq: '🔥 Plancha/BBQ',
  festif: '🎉 Festif',
  rapide: '⚡ Rapide',
  'vege-option': '🥬 Option végé',
  perso: '✍️ Mon menu',
}

export default function Menus() {
  const menusQ = useMenus()

  if (menusQ.isLoading) return <p className="text-ocean-700 italic">Chargement…</p>
  if (!menusQ.data) return <p className="text-coral-700">Impossible de charger les menus.</p>

  const jours = menusQ.data.jours

  return (
    <div className="space-y-4">
      {/* === Intro === */}
      <section className="card p-4 sm:p-5">
        <h2 className="text-lg sm:text-xl font-bold text-ocean-900 flex items-center gap-2">
          <span>🍲</span>
          <span>Menus suggérés</span>
        </h2>
        <p className="text-xs sm:text-sm text-ocean-700 mt-1">
          Des idées de menus pour chaque repas du séjour, calculées automatiquement d'après&nbsp;:
        </p>
        <ul className="flex flex-wrap gap-1.5 mt-2 text-[11px] sm:text-xs">
          <li className="px-2 py-1 rounded-full bg-ocean-50 border border-ocean-100 text-ocean-800 font-semibold">
            👥 le nombre de convives (onglet <Link to="/repas" className="underline hover:text-coral-600">Repas</Link>)
          </li>
          <li className="px-2 py-1 rounded-full bg-ocean-50 border border-ocean-100 text-ocean-800 font-semibold">
            🧑🧒👶 la répartition adulte/ado · enfant · bébé
          </li>
          <li className="px-2 py-1 rounded-full bg-ocean-50 border border-ocean-100 text-ocean-800 font-semibold">
            🌤️ la météo du jour à Hossegor
          </li>
          <li className="px-2 py-1 rounded-full bg-ocean-50 border border-ocean-100 text-ocean-800 font-semibold">
            💶 le budget courses estimé
          </li>
          <li className="px-2 py-1 rounded-full bg-ocean-50 border border-ocean-100 text-ocean-800 font-semibold">
            🔄 la variété d'un jour à l'autre
          </li>
        </ul>
        <p className="text-[11px] text-ocean-600/80 italic mt-2">
          Ce sont des <strong>suggestions</strong> — libre au chef du jour d'improviser !
          La météo n'est disponible que ~15 jours à l'avance.
        </p>
        <div className="mt-3">
          <a
            href="/api/v1/menus/export.xlsx"
            download
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ocean-600 text-white
                       text-xs sm:text-sm font-semibold hover:bg-ocean-700 transition shadow-sm"
          >
            📥 Télécharger le planning (Excel)
          </a>
          <span className="ml-2 text-[10px] text-ocean-600/70 italic">
            jour, convives, responsables, menus, ingrédients, recettes, météo
          </span>
        </div>
      </section>

      {/* === Réglages : objectif de coût + adresse + rayon === */}
      <ReglagesCard />

      {/* === Commerces autour de la maison === */}
      <CommercesCard />

      {/* === Responsables : attribution automatique des binômes === */}
      <ResponsablesAutoCard jours={jours} />

      {/* === Jours === */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
        {jours.map(j => (
          <JourCard
            key={j.date}
            jour={j}
            prefsDisponibles={menusQ.data.prefs_disponibles ?? []}
            catalogue={menusQ.data.catalogue ?? []}
            menusPersos={menusQ.data.menus_persos ?? []}
          />
        ))}
      </div>
    </div>
  )
}

/* ============================================================================
   Responsables : attribution automatique d'un binôme adultes/ados par journée
   ========================================================================= */
function ResponsablesAutoCard({ jours }: { jours: MenuJour[] }) {
  const auto = useAutoResponsables()
  const personnesQ = usePersonnes()
  const avatarVersion = (pid: number) =>
    personnesQ.data?.find(p => p.id === pid)?.avatar_version ?? 0

  const dejaAttribues = jours.some(j =>
    (['M', 'S'] as const).some(m => (j.repas[m]?.responsables?.length ?? 0) > 0),
  )

  const lancer = () => {
    if (
      dejaAttribues &&
      !confirm(
        'Des responsables sont déjà attribués : l\'attribution automatique va les remplacer '
        + 'sur TOUS les repas du séjour. Continuer ?',
      )
    ) return
    auto.mutate()
  }

  return (
    <section className="card p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-bold text-ocean-900 flex items-center gap-2">
            <span>👨‍🍳</span>
            <span>Responsables des repas</span>
          </h2>
          <p className="text-[11px] sm:text-xs text-ocean-700 mt-0.5 leading-snug">
            Attribue automatiquement un <strong>binôme d'adultes/ados par journée</strong> (midi + soir),
            équitablement selon la <strong>durée de présence</strong> de chacun — quelqu'un présent
            2-3 jours recevra moins de journées que les présents tout le séjour.
            Chaque repas reste ensuite modifiable via « Attribuer ce repas ».
          </p>
        </div>
        <button
          type="button"
          disabled={auto.isPending}
          onClick={lancer}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-coral-500 text-white
                     text-xs sm:text-sm font-semibold hover:bg-coral-600 transition shadow-sm disabled:opacity-50"
        >
          {auto.isPending
            ? <span className="inline-block w-3.5 h-3.5 rounded-full border-2 border-white border-t-transparent animate-spin" />
            : <span aria-hidden>🪄</span>}
          Attribuer automatiquement
        </button>
      </div>

      {auto.isError && (
        <p className="mt-2 text-xs text-coral-700 font-semibold">
          Impossible d'attribuer les binômes pour le moment — réessaie.
        </p>
      )}

      {/* Bilan de la dernière attribution : X journées par personne */}
      {auto.data && (
        <div className="mt-3 pt-2 border-t border-ocean-50 anim-in">
          <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-700 mb-1.5">
            ✅ Binômes attribués — répartition ({auto.data.jours.length} jours)
          </div>
          <ul className="flex flex-wrap gap-1.5">
            {auto.data.repartition.map(r => (
              <li
                key={r.personne_id}
                className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full bg-ocean-50 border border-ocean-100"
                title={`${r.prenom} : ${r.jours_attribues} journée${r.jours_attribues > 1 ? 's' : ''} pour ${r.jours_presence} jour${r.jours_presence > 1 ? 's' : ''} de présence`}
              >
                <Avatar personne_id={r.personne_id} prenom={r.prenom}
                        avatar_version={avatarVersion(r.personne_id)} size="xs" />
                <span className="text-[11px] font-semibold text-ocean-900">{r.prenom}</span>
                <span className="text-[10px] text-ocean-600">
                  {r.jours_attribues} j / {r.jours_presence} j prés.
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

/* ============================================================================
   Réglages : objectif de coût moyen par repas, adresse maison, rayon
   ========================================================================= */
function ReglagesCard() {
  const reglagesQ = useReglagesMenus()
  const menusQ = useMenus()
  const save = useSetReglagesMenus()
  const [objectif, setObjectif] = useState<string>('')
  const [adresse, setAdresse] = useState<string>('')
  const [rayon, setRayon] = useState<string>('5')
  const [prefs, setPrefs] = useState<string[]>(['selon_meteo'])
  const [saved, setSaved] = useState(false)
  const prefsDisponibles = menusQ.data?.prefs_disponibles ?? []

  const togglePref = (key: string) =>
    setPrefs(p => (p.includes(key) ? p.filter(k => k !== key) : [...p, key]))

  // === Autocomplete d'adresse ==============================================
  const [suggestions, setSuggestions] = useState<AdresseSuggestion[]>([])
  const [showSugg, setShowSugg] = useState(false)
  // true uniquement quand l'utilisateur TAPE (pas à l'hydratation ni à la sélection)
  const typingRef = useRef(false)

  useEffect(() => {
    if (!typingRef.current) return
    const q = adresse.trim()
    if (q.length < 3) {
      setSuggestions([])
      setShowSugg(false)
      return
    }
    const t = setTimeout(() => {
      fetchAdresseSuggestions(q)
        .then(r => {
          setSuggestions(r.suggestions)
          setShowSugg(r.suggestions.length > 0)
        })
        .catch(() => {})
    }, 400) // debounce : respecte la politique Nominatim (1 req/s)
    return () => clearTimeout(t)
  }, [adresse])

  const pickSuggestion = (s: AdresseSuggestion) => {
    typingRef.current = false
    setAdresse(s.label)
    setSuggestions([])
    setShowSugg(false)
  }

  // Hydrate le formulaire dès que les réglages arrivent (une seule fois par fetch)
  useEffect(() => {
    if (!reglagesQ.data) return
    typingRef.current = false
    setObjectif(reglagesQ.data.objectif_cout_repas != null ? String(reglagesQ.data.objectif_cout_repas) : '')
    setAdresse(reglagesQ.data.adresse_maison)
    setRayon(String(reglagesQ.data.rayon_recherche_km))
    if (reglagesQ.data.prefs_sejour) setPrefs(reglagesQ.data.prefs_sejour)
  }, [reglagesQ.data])

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setSaved(false)
    const obj = objectif.trim() === '' ? null : Number(objectif.replace(',', '.'))
    save.mutate(
      {
        objectif_cout_repas: obj != null && !Number.isNaN(obj) ? obj : null,
        adresse_maison: adresse.trim(),
        rayon_recherche_km: Math.min(50, Math.max(0.5, Number(rayon.replace(',', '.')) || 5)),
        prefs_sejour: prefs,
      },
      { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 3000) } },
    )
  }

  return (
    <section className="card p-4 sm:p-5">
      <h3 className="font-bold text-ocean-900 flex items-center gap-2 mb-1">
        <span>⚙️</span>
        <span>Réglages des menus</span>
      </h3>
      <p className="text-[11px] text-ocean-600/80 mb-3">
        L'objectif de coût influence les suggestions (les menus dans le budget sont favorisés).
        L'adresse sert à localiser les commerces alentour.
      </p>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-[160px_1fr_120px_auto] gap-2 items-end">
        <label className="flex flex-col text-[11px] font-semibold text-ocean-700">
          <span className="mb-0.5">🎯 Objectif €/repas/adulte</span>
          <input
            type="number"
            min={1}
            max={100}
            step="0.5"
            value={objectif}
            onChange={e => setObjectif(e.target.value)}
            placeholder="ex : 7"
            className="input py-1.5 text-sm"
          />
        </label>
        <label className="relative flex flex-col text-[11px] font-semibold text-ocean-700">
          <span className="mb-0.5">📍 Adresse de la maison</span>
          <input
            type="text"
            value={adresse}
            onChange={e => { typingRef.current = true; setAdresse(e.target.value) }}
            onBlur={() => setTimeout(() => setShowSugg(false), 150)}
            onFocus={() => { if (suggestions.length > 0 && typingRef.current) setShowSugg(true) }}
            placeholder="ex : 123 avenue de la Plage, 40150 Soorts-Hossegor"
            maxLength={255}
            autoComplete="off"
            className="input py-1.5 text-sm"
          />
          {/* Suggestions Nominatim (proxy backend) */}
          {showSugg && suggestions.length > 0 && (
            <ul className="absolute top-full left-0 right-0 mt-1 z-40 bg-white border border-ocean-200 rounded-lg shadow-card overflow-hidden anim-in">
              {suggestions.map((s, i) => (
                <li key={i}>
                  <button
                    type="button"
                    // onMouseDown : se déclenche AVANT le blur de l'input
                    onMouseDown={() => pickSuggestion(s)}
                    className="w-full text-left px-3 py-2 text-xs text-ocean-800 hover:bg-ocean-50 border-b border-ocean-50 last:border-0 font-normal leading-snug"
                  >
                    📍 {s.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </label>
        <label className="flex flex-col text-[11px] font-semibold text-ocean-700">
          <span className="mb-0.5">📏 Rayon (km)</span>
          <input
            type="number"
            min={0.5}
            max={50}
            step="0.5"
            value={rayon}
            onChange={e => setRayon(e.target.value)}
            className="input py-1.5 text-sm"
          />
        </label>
        <button
          type="submit"
          disabled={save.isPending}
          className="btn-primary py-1.5 px-4 text-sm disabled:opacity-60"
        >
          {save.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </button>

        {/* Préférences alimentaires pour TOUT le séjour */}
        <div className="sm:col-span-4">
          <div className="text-[11px] font-semibold text-ocean-700 mt-1 mb-1">
            🍽️ Préférences pour tous les menus du séjour
            <span className="font-normal text-ocean-500 ml-1">(les régimes/allergies filtrent, les autres orientent)</span>
          </div>
          <PrefsChips
            disponibles={prefsDisponibles}
            actives={prefs}
            onToggle={togglePref}
            disabled={save.isPending}
          />
        </div>
      </form>
      {saved && (
        <p className="text-[11px] text-emerald-700 font-semibold mt-2 anim-in">
          ✓ Réglages enregistrés{reglagesQ.data?.geocodee === false && adresse.trim() !== ''
            ? ' — adresse non localisée, vérifie l\'orthographe (n° + rue + code postal + ville).'
            : ''}
        </p>
      )}
      {reglagesQ.data && !reglagesQ.data.geocodee && reglagesQ.data.adresse_maison !== '' && !saved && (
        <p className="text-[11px] text-coral-700 font-semibold mt-2">
          ⚠ L'adresse enregistrée n'a pas pu être localisée — les commerces ne peuvent pas être cherchés.
          Essaie un format « n° rue, code postal ville ».
        </p>
      )}
    </section>
  )
}

/* ============================================================================
   Commerces autour de la maison (OpenStreetMap)
   ========================================================================= */
const TYPE_COMMERCE_ICON: Record<string, string> = {
  'Supérette': '🏪',
  'Supermarché': '🛒',
  'Hypermarché': '🏬',
}

function CommercesCard() {
  const commercesQ = useCommerces()
  const reglagesQ = useReglagesMenus()
  const [showAll, setShowAll] = useState(false)
  const [selected, setSelected] = useState<Commerce | null>(null)

  const d = commercesQ.data
  const maisonLat = reglagesQ.data?.maison_lat ?? null
  const maisonLon = reglagesQ.data?.maison_lon ?? null
  if (commercesQ.isLoading) {
    return (
      <section className="card p-4 sm:p-5">
        <h3 className="font-bold text-ocean-900 flex items-center gap-2">
          <span>🛒</span><span>Commerces autour de la maison</span>
        </h3>
        <p className="text-sm text-ocean-500 italic mt-1">Recherche en cours…</p>
      </section>
    )
  }
  if (!d) return null

  if (!d.geocodee) {
    return (
      <section className="card p-4 sm:p-5">
        <h3 className="font-bold text-ocean-900 flex items-center gap-2 mb-1">
          <span>🛒</span><span>Commerces autour de la maison</span>
        </h3>
        <p className="text-sm text-ocean-600">
          Renseigne l'<strong>adresse de la maison</strong> dans les réglages ci-dessus pour découvrir
          les supérettes, supermarchés et hypermarchés à proximité.
        </p>
      </section>
    )
  }

  const list = showAll ? d.commerces : d.commerces.slice(0, 6)
  return (
    <section className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3 className="font-bold text-ocean-900 flex items-center gap-2">
          <span>🛒</span><span>Commerces autour de la maison</span>
        </h3>
        <span className="text-[11px] text-ocean-500 shrink-0 pt-1">
          rayon {d.rayon_km} km · {d.commerces.length} trouvé{d.commerces.length > 1 ? 's' : ''}
        </span>
      </div>
      <p className="text-[11px] text-ocean-600/80 mb-3">
        Clique sur « Site & prix » pour consulter les produits, prix et drive de chaque enseigne,
        ou « Itinéraire » pour t'y rendre. (Source : OpenStreetMap)
      </p>

      {/* === Carte interactive : maison + rayon + commerces === */}
      {maisonLat != null && maisonLon != null && (
        <div className="mb-3">
          <CarteCommerces
            maisonLat={maisonLat}
            maisonLon={maisonLon}
            rayonKm={d.rayon_km}
            commerces={d.commerces}
            onSelect={setSelected}
          />
          <p className="text-[10px] text-ocean-500 italic mt-1">
            🏠 = la maison · cercle = rayon de recherche · clique sur un commerce pour
            l'adresse, les coordonnées et les itinéraires.
          </p>
        </div>
      )}

      {/* === Panneau détail du commerce sélectionné sur la carte === */}
      {selected && maisonLat != null && (
        <CommerceDetailPanel commerce={selected} onClose={() => setSelected(null)} />
      )}

      {d.commerces.length === 0 ? (
        <p className="text-sm text-ocean-500 italic">
          Aucun commerce alimentaire trouvé dans ce rayon — élargis le rayon dans les réglages.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {list.map((c, i) => <CommerceRow key={`${c.nom}-${i}`} commerce={c} onDetail={() => setSelected(c)} />)}
          </div>
          {d.commerces.length > 6 && (
            <button
              type="button"
              onClick={() => setShowAll(v => !v)}
              className="mt-2 text-[12px] font-semibold text-ocean-600 hover:text-coral-600 transition"
            >
              {showAll ? '▾ Réduire' : `▸ Voir les ${d.commerces.length - 6} autres commerces`}
            </button>
          )}
        </>
      )}
    </section>
  )
}

/* ============================================================================
   Carte Leaflet : maison + cercle rayon + marqueurs commerces
   ========================================================================= */
function CarteCommerces({ maisonLat, maisonLon, rayonKm, commerces, onSelect }: {
  maisonLat: number
  maisonLon: number
  rayonKm: number
  commerces: Commerce[]
  onSelect: (c: Commerce) => void
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const layerRef = useRef<L.LayerGroup | null>(null)
  // Callback dans une ref → les marqueurs n'ont pas besoin d'être reconstruits
  // quand l'identité de onSelect change à chaque render.
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  // Création de la carte (une seule fois)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, { scrollWheelZoom: false })
    map.setView([maisonLat, maisonLon], 14)
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map)
    mapRef.current = map
    return () => {
      map.remove()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // (Re)dessine maison + cercle + commerces quand les données changent
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    layerRef.current?.remove()
    const group = L.layerGroup()

    const maisonIcon = L.divIcon({
      html: '<div class="map-emoji map-emoji-maison">🏠</div>',
      className: '', iconSize: [34, 34], iconAnchor: [17, 17],
    })
    L.marker([maisonLat, maisonLon], { icon: maisonIcon, zIndexOffset: 1000 })
      .bindTooltip('La maison', { direction: 'top' })
      .addTo(group)

    L.circle([maisonLat, maisonLon], {
      radius: rayonKm * 1000,
      color: '#0e7490', weight: 1.5, fillColor: '#0e7490', fillOpacity: 0.05,
    }).addTo(group)

    commerces.forEach(c => {
      const emoji = TYPE_COMMERCE_ICON[c.type] ?? '🛒'
      // Les favoris portent une étoile et passent au-dessus des autres marqueurs
      const icon = L.divIcon({
        html: c.favori
          ? `<div class="map-emoji map-emoji-favori">${emoji}<span class="map-star">⭐</span></div>`
          : `<div class="map-emoji">${emoji}</div>`,
        className: '', iconSize: [28, 28], iconAnchor: [14, 14],
      })
      L.marker([c.lat, c.lon], { icon, zIndexOffset: c.favori ? 500 : 0 })
        .bindTooltip(
          `${c.favori ? '⭐ ' : ''}${c.nom} · ${c.distance_km < 1 ? Math.round(c.distance_km * 1000) + ' m' : c.distance_km.toFixed(1) + ' km'}`,
          { direction: 'top' },
        )
        .on('click', () => onSelectRef.current(c))
        .addTo(group)
    })

    group.addTo(map)
    layerRef.current = group
    // Cadre la vue sur le cercle du rayon
    map.fitBounds(L.latLng(maisonLat, maisonLon).toBounds(rayonKm * 2000), { padding: [16, 16] })
  }, [maisonLat, maisonLon, rayonKm, commerces])

  return <div ref={containerRef} className="h-72 sm:h-96 w-full rounded-xl border border-ocean-200 z-0" />
}

/* ============================================================================
   Panneau détail : adresse, coordonnées, itinéraires voiture / à pied / transit
   ========================================================================= */
function CommerceDetailPanel({ commerce: c, onClose }: {
  commerce: Commerce
  onClose: () => void
}) {
  const itiQ = useItineraire(c.lat, c.lon)

  return (
    <div className="mb-3 rounded-xl border-2 border-coral-200 bg-coral-50/40 p-3 sm:p-4 anim-in">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="font-bold text-ocean-900 leading-tight">
            <span aria-hidden className="mr-1">{TYPE_COMMERCE_ICON[c.type] ?? '🛒'}</span>
            {c.nom}
          </h4>
          <div className="text-[11px] text-ocean-700 mt-0.5">
            {c.type}{c.enseigne && c.enseigne !== c.nom ? ` · ${c.enseigne}` : ''}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="shrink-0 w-7 h-7 rounded-full text-ocean-500 hover:text-coral-600 hover:bg-white flex items-center justify-center font-bold transition"
        >
          ✕
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2 text-xs text-ocean-800">
        <div className="bg-white rounded-lg border border-ocean-100 px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-600 mb-0.5">Adresse</div>
          {c.adresse || <span className="italic text-ocean-400">non renseignée sur OpenStreetMap</span>}
          {c.horaires && <div className="text-[10px] text-ocean-500 mt-1">🕒 {c.horaires}</div>}
        </div>
        <div className="bg-white rounded-lg border border-ocean-100 px-2.5 py-1.5">
          <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-600 mb-0.5">Coordonnées GPS</div>
          <span className="font-mono">{c.lat.toFixed(5)}, {c.lon.toFixed(5)}</span>
          <div className="text-[10px] text-ocean-500 mt-1">
            à {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)} m` : `${c.distance_km.toFixed(1)} km`} de la maison (vol d'oiseau)
          </div>
        </div>
      </div>

      {/* Itinéraires */}
      <div className="mt-2">
        <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-600 mb-1">
          Itinéraires depuis la maison
        </div>
        {itiQ.isLoading ? (
          <p className="text-xs text-ocean-500 italic flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-full border-2 border-ocean-400 border-t-transparent animate-spin" />
            Calcul des trajets…
          </p>
        ) : itiQ.data ? (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            <a
              href={itiQ.data.voiture_url}
              target="_blank" rel="noopener noreferrer"
              className="rounded-lg bg-white border border-ocean-200 hover:border-ocean-400 px-2.5 py-2 transition"
            >
              <div className="font-bold text-ocean-900 text-sm">🚗 Voiture</div>
              {itiQ.data.voiture ? (
                <div className="text-xs text-ocean-700">
                  ≈ <strong>{Math.round(itiQ.data.voiture.duree_min)} min</strong> · {itiQ.data.voiture.distance_km.toFixed(1)} km
                </div>
              ) : (
                <div className="text-[11px] text-ocean-500 italic">durée indisponible — voir le lien</div>
              )}
            </a>
            <a
              href={itiQ.data.pied_url}
              target="_blank" rel="noopener noreferrer"
              className="rounded-lg bg-white border border-ocean-200 hover:border-ocean-400 px-2.5 py-2 transition"
            >
              <div className="font-bold text-ocean-900 text-sm">🚶 À pied</div>
              {itiQ.data.pied ? (
                <div className="text-xs text-ocean-700">
                  ≈ <strong>{Math.round(itiQ.data.pied.duree_min)} min</strong> · {itiQ.data.pied.distance_km.toFixed(1)} km
                </div>
              ) : (
                <div className="text-[11px] text-ocean-500 italic">durée indisponible — voir le lien</div>
              )}
            </a>
            <a
              href={itiQ.data.transit_url}
              target="_blank" rel="noopener noreferrer"
              className="rounded-lg bg-white border border-ocean-200 hover:border-ocean-400 px-2.5 py-2 transition"
            >
              <div className="font-bold text-ocean-900 text-sm">🚌 Transports</div>
              <div className="text-[11px] text-ocean-700 leading-snug">
                Horaires, prochains départs et heures d'arrivée
              </div>
            </a>
          </div>
        ) : (
          <p className="text-xs text-coral-700">Itinéraires indisponibles pour le moment.</p>
        )}
        {c.site_web && (
          <a
            href={c.site_web}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 mt-2 px-2.5 py-1 rounded-md bg-coral-100 border border-coral-300 text-coral-800 text-xs font-bold hover:bg-coral-200 transition"
          >
            🌐 Site du magasin — produits & prix
          </a>
        )}
      </div>
    </div>
  )
}

/* ============================================================================
   Formulaire « mon propre menu » — ingrédients par adulte, mis à l'échelle
   automatiquement selon les convives de chaque repas.
   ========================================================================= */
const UNITES = ['g', 'kg', 'cl', 'L', 'pièce', 'tranches', 'part', 'c. à soupe', 'sachet', 'boîte']

function CreerMenuPersoForm({ moment, repasId, onDone }: {
  moment: 'M' | 'S'
  repasId: number
  onDone: () => void
}) {
  const create = useCreateMenuPerso()
  const [nom, setNom] = useState('')
  const [description, setDescription] = useState('')
  const [prix, setPrix] = useState('')
  const [pourTous, setPourTous] = useState(true) // MS = utilisable midi ET soir
  const [ings, setIngs] = useState<Array<{ nom: string; qte: string; unite: string }>>([
    { nom: '', qte: '', unite: 'g' },
  ])
  const [recette, setRecette] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const [suggLoading, setSuggLoading] = useState(false)
  const [suggInfo, setSuggInfo] = useState<string | null>(null)

  // === Suggestion AUTOMATIQUE des ingrédients d'après le nom du menu ========
  const suggerer = async () => {
    setErr(null)
    setSuggInfo(null)
    setSuggLoading(true)
    try {
      const r = await fetchSuggestionIngredients(nom.trim())
      if (r.source === 'aucune' || r.ingredients.length === 0) {
        setSuggInfo('Nom trop court — indique d\'abord le nom du plat (3 caractères min).')
        return
      }
      setIngs(r.ingredients.map(i => ({ nom: i.nom, qte: String(i.qte), unite: i.unite })))
      if (r.prix_adulte != null && prix.trim() === '') {
        setPrix(String(r.prix_adulte))
      }
      setSuggInfo(
        r.source === 'generique'
          ? '✨ Trame générique proposée (plat non reconnu) — personnalise les lignes ci-dessous.'
          : `✨ Ingrédients générés d'après « ${r.match} » — ajuste si besoin.`,
      )
    } catch {
      setSuggInfo('Impossible de générer les ingrédients pour le moment.')
    } finally {
      setSuggLoading(false)
    }
  }

  const setIng = (i: number, patch: Partial<{ nom: string; qte: string; unite: string }>) =>
    setIngs(list => list.map((ing, idx) => (idx === i ? { ...ing, ...patch } : ing)))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setErr(null)
    if (nom.trim().length < 2) {
      setErr('Donne un nom au menu (2 caractères minimum).')
      return
    }
    const ingredients = ings
      .filter(ing => ing.nom.trim() !== '')
      .map(ing => ({
        nom: ing.nom.trim(),
        qte: Number(String(ing.qte).replace(',', '.')),
        unite: ing.unite,
      }))
    if (ingredients.some(ing => !Number.isFinite(ing.qte) || ing.qte <= 0)) {
      setErr('Chaque ingrédient doit avoir une quantité (par adulte) supérieure à 0.')
      return
    }
    const prixNum = prix.trim() === '' ? 0 : Number(prix.replace(',', '.'))
    create.mutate(
      {
        nom: nom.trim(),
        description: description.trim(),
        moment: pourTous ? 'MS' : moment,
        prix_adulte: Number.isFinite(prixNum) && prixNum > 0 ? prixNum : 0,
        ingredients,
        recette: recette.trim(),
        repas_id: repasId, // créé ET utilisé pour ce repas en un clic
      },
      { onSuccess: onDone },
    )
  }

  return (
    <form onSubmit={submit} className="mt-2 space-y-2 anim-in">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_110px] gap-2">
        <label className="flex flex-col text-[11px] font-semibold text-coral-800">
          <span className="mb-0.5">Nom du menu *</span>
          <input
            type="text"
            value={nom}
            onChange={e => setNom(e.target.value)}
            placeholder="ex : Poulet rôti du dimanche"
            maxLength={120}
            className="input py-1.5 text-sm"
          />
        </label>
        <label className="flex flex-col text-[11px] font-semibold text-coral-800">
          <span className="mb-0.5">Prix €/adulte</span>
          <input
            type="number"
            min={0}
            max={200}
            step="0.5"
            value={prix}
            onChange={e => setPrix(e.target.value)}
            placeholder="optionnel"
            className="input py-1.5 text-sm"
          />
        </label>
      </div>
      <label className="flex flex-col text-[11px] font-semibold text-coral-800">
        <span className="mb-0.5">Description</span>
        <input
          type="text"
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="ex : Poulet rôti, pommes grenailles, haricots verts · tarte aux pommes"
          maxLength={300}
          className="input py-1.5 text-sm"
        />
      </label>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-1.5 mb-1">
          <div className="text-[11px] font-semibold text-coral-800">
            Ingrédients — quantités <strong>PAR ADULTE</strong>
            <span className="font-normal text-coral-700/80"> (auto-ajustées au nombre de convives ; enfant = 60 %)</span>
          </div>
          <button
            type="button"
            disabled={suggLoading || nom.trim().length < 3}
            onClick={suggerer}
            title={nom.trim().length < 3 ? 'Indique d\'abord le nom du plat' : 'Générer les ingrédients d\'après le nom du plat'}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white border border-coral-300 text-coral-800 text-[11px] font-bold hover:bg-coral-100 transition disabled:opacity-40"
          >
            {suggLoading
              ? <span className="inline-block w-3 h-3 rounded-full border-2 border-coral-600 border-t-transparent animate-spin" />
              : <span aria-hidden>✨</span>}
            Suggérer automatiquement
          </button>
        </div>
        {suggInfo && (
          <p className="text-[10px] text-coral-700 font-semibold mb-1 anim-in">{suggInfo}</p>
        )}
        <div className="space-y-1.5">
          {ings.map((ing, i) => (
            <div key={i} className="grid grid-cols-[1fr_70px_90px_28px] gap-1.5 items-center">
              <input
                type="text"
                value={ing.nom}
                onChange={e => setIng(i, { nom: e.target.value })}
                placeholder="ex : Poulet"
                maxLength={60}
                className="input py-1 text-xs"
              />
              <input
                type="number"
                min={0}
                step="any"
                value={ing.qte}
                onChange={e => setIng(i, { qte: e.target.value })}
                placeholder="qté"
                className="input py-1 text-xs"
              />
              <select
                value={ing.unite}
                onChange={e => setIng(i, { unite: e.target.value })}
                className="input py-1 text-xs"
              >
                {UNITES.map(u => <option key={u} value={u}>{u}</option>)}
              </select>
              <button
                type="button"
                onClick={() => setIngs(list => list.filter((_, idx) => idx !== i))}
                disabled={ings.length <= 1}
                aria-label="Retirer cet ingrédient"
                className="w-6 h-6 rounded text-coral-600 hover:bg-coral-100 disabled:opacity-30 text-xs font-bold"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setIngs(list => [...list, { nom: '', qte: '', unite: 'g' }])}
          className="mt-1.5 text-[11px] font-semibold text-coral-700 hover:text-coral-900 transition"
        >
          + Ajouter un ingrédient
        </button>
      </div>

      <label className="flex flex-col text-[11px] font-semibold text-coral-800">
        <span className="mb-0.5">
          📖 Recette <span className="font-normal text-coral-700/80">(optionnel — une étape par ligne)</span>
        </span>
        <textarea
          value={recette}
          onChange={e => setRecette(e.target.value)}
          placeholder={'ex :\nPréchauffer le four à 200 °C\nAssaisonner le poulet et enfourner 1 h 10\nServir avec les grenailles rôties'}
          maxLength={4000}
          rows={4}
          className="input py-1.5 text-xs leading-snug resize-y"
        />
      </label>

      <label className="flex items-center gap-2 text-[11px] text-coral-800 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={pourTous}
          onChange={e => setPourTous(e.target.checked)}
          className="accent-coral-500 w-3.5 h-3.5"
        />
        Utilisable midi <strong>et</strong> soir (sinon : {moment === 'M' ? 'midi' : 'soir'} uniquement)
      </label>

      {err && <p className="text-[11px] text-coral-700 font-bold">{err}</p>}

      <button
        type="submit"
        disabled={create.isPending}
        className="btn-coral w-full py-1.5 text-sm disabled:opacity-60"
      >
        {create.isPending ? 'Création…' : '✍️ Créer et utiliser pour ce repas'}
      </button>
    </form>
  )
}

function CommerceRow({ commerce: c, onDetail }: { commerce: Commerce; onDetail: () => void }) {
  const favoriMut = useToggleFavoriCommerce()
  return (
    <div className={`rounded-lg border px-3 py-2 flex flex-col gap-1 ${
      c.favori ? 'border-amber-300 bg-amber-50/50' : 'border-ocean-100 bg-white'
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <button
            type="button"
            onClick={onDetail}
            className="font-bold text-ocean-900 text-sm leading-tight truncate hover:text-coral-600 transition text-left"
            title="Voir adresse, coordonnées et itinéraires"
          >
            <span aria-hidden className="mr-1">{TYPE_COMMERCE_ICON[c.type] ?? '🛒'}</span>
            {c.nom}
            {c.favori && (
              <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded bg-amber-100 border border-amber-300 text-amber-800 text-[9px] font-bold uppercase tracking-wide">
                ⭐ Favori
              </span>
            )}
          </button>
          <div className="text-[10px] text-ocean-600 mt-0.5">
            {c.type}{c.enseigne && c.enseigne !== c.nom ? ` · ${c.enseigne}` : ''}
            {c.adresse && <span className="text-ocean-500"> · {c.adresse}</span>}
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-1">
          {c.osm_id && (
            <button
              type="button"
              disabled={favoriMut.isPending}
              onClick={() => favoriMut.mutate({ osm_id: c.osm_id!, favori: !c.favori })}
              aria-pressed={!!c.favori}
              aria-label={c.favori ? `Retirer ${c.nom} des favoris` : `Ajouter ${c.nom} aux favoris`}
              title={c.favori ? 'Retirer des favoris' : 'Marquer comme favori'}
              className={`w-7 h-7 rounded-full flex items-center justify-center text-base transition disabled:opacity-50 ${
                c.favori ? 'text-amber-500 hover:bg-amber-100' : 'text-ocean-300 hover:text-amber-500 hover:bg-amber-50'
              }`}
            >
              {c.favori ? '⭐' : '☆'}
            </button>
          )}
          <span className="px-1.5 py-0.5 rounded-full bg-ocean-50 border border-ocean-100 text-[10px] font-bold text-ocean-800">
            {c.distance_km < 1 ? `${Math.round(c.distance_km * 1000)} m` : `${c.distance_km.toFixed(1)} km`}
          </span>
        </div>
      </div>
      {c.horaires && (
        <div className="text-[10px] text-ocean-500 truncate" title={c.horaires}>🕒 {c.horaires}</div>
      )}
      <div className="flex gap-1.5 mt-0.5">
        {c.site_web && (
          <a
            href={c.site_web}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-coral-50 border border-coral-200 text-coral-800 text-[11px] font-semibold hover:bg-coral-100 transition"
          >
            🌐 Site & prix
          </a>
        )}
        <a
          href={c.itineraire}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-ocean-50 border border-ocean-200 text-ocean-800 text-[11px] font-semibold hover:bg-ocean-100 transition"
        >
          🗺️ Itinéraire
        </a>
      </div>
    </div>
  )
}

/* ============================================================================
   Chips de préférences (séjour et par jour)
   ========================================================================= */
function PrefsChips({ disponibles, actives, onToggle, disabled }: {
  disponibles: PrefDisponible[]
  actives: string[]
  onToggle: (key: string) => void
  disabled?: boolean
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {disponibles.map(p => {
        const on = actives.includes(p.key)
        return (
          <button
            key={p.key}
            type="button"
            disabled={disabled}
            onClick={() => onToggle(p.key)}
            aria-pressed={on}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[11px] font-semibold transition disabled:opacity-50 ${
              on
                ? 'bg-ocean-600 border-ocean-700 text-white shadow-soft'
                : 'bg-white border-ocean-200 text-ocean-700 hover:border-ocean-400'
            }`}
            title={p.type === 'hard' ? 'Filtre strict (régime/allergie)' : 'Préférence (oriente les suggestions)'}
          >
            <span aria-hidden>{p.icon}</span>
            <span>{p.label}</span>
            {on && <span aria-hidden>✓</span>}
          </button>
        )
      })}
    </div>
  )
}

function JourCard({ jour, prefsDisponibles, catalogue, menusPersos }: {
  jour: MenuJour
  prefsDisponibles: PrefDisponible[]
  catalogue: MenuCatalogueItem[]
  menusPersos: MenuPersoOut[]
}) {
  /* NB : les préférences PAR REPAS sont gérées dans chaque RepasBlock. */
  const [showPrefs, setShowPrefs] = useState(false)
  const [showJourPicker, setShowJourPicker] = useState(false)
  const setPrefsJour = useSetPrefsJour()
  const setRespJour = useSetResponsablesJour()
  const personnesQ = usePersonnes()
  const hasConvives =
    (jour.repas.M && jour.repas.M.nb_adulte_ado + jour.repas.M.nb_enfant > 0) ||
    (jour.repas.S && jour.repas.S.nb_adulte_ado + jour.repas.S.nb_enfant > 0)

  // Binôme de la JOURNÉE : affiché en tête quand midi et soir partagent
  // exactement les mêmes responsables (cas de l'attribution automatique).
  const respM = jour.repas.M?.responsables ?? []
  const respS = jour.repas.S?.responsables ?? []
  const ids = (list: MenuResponsable[]) => list.map(r => r.personne_id).sort((a, b) => a - b).join(',')
  const memeBinome = ids(respM) === ids(respS)
  const binomeJour: MenuResponsable[] = respM.length > 0 && memeBinome ? respM : []
  const mixte = !memeBinome && (respM.length > 0 || respS.length > 0)

  // Sélection « journée » = personnes responsables du midi ET du soir
  const jourIds = respM
    .filter(r => respS.some(s => s.personne_id === r.personne_id))
    .map(r => r.personne_id)
  const personnesActives = (personnesQ.data ?? [])
    .filter(p => p.actif)
    .sort((a, b) => a.ordre - b.ordre || a.prenom.localeCompare(b.prenom))

  const toggleJour = (pid: number) => {
    const actif = jourIds.includes(pid)
    // Au moins UNE personne doit rester responsable de la journée
    if (actif && jourIds.length <= 1) return
    const next = actif ? jourIds.filter(i => i !== pid) : [...jourIds, pid]
    if (next.length === 0) return
    setRespJour.mutate({ date: jour.date, personne_ids: next })
  }

  const togglePrefJour = (key: string) => {
    const next = jour.prefs_jour.includes(key)
      ? jour.prefs_jour.filter(k => k !== key)
      : [...jour.prefs_jour, key]
    setPrefsJour.mutate({ date: jour.date, prefs: next })
  }

  return (
    <div className="card p-3 sm:p-4">
      {/* En-tête jour + météo + préférences du jour */}
      <div className="flex items-start justify-between gap-2 pb-2 border-b border-ocean-100/70 mb-3">
        <div className="min-w-0">
          <h3 className="font-bold text-ocean-900 capitalize leading-tight">
            {fmtDate(jour.date)}
          </h3>
          {hasConvives && (
            <button
              type="button"
              onClick={() => setShowJourPicker(v => !v)}
              className="flex items-center gap-1.5 mt-1 flex-wrap text-left hover:opacity-80 transition"
              aria-expanded={showJourPicker}
              title="Attribuer la journée entière (midi + soir) — clique pour modifier"
            >
              <span aria-hidden className="text-xs">👨‍🍳</span>
              {binomeJour.length > 0 ? (
                binomeJour.map(r => (
                  <span key={r.personne_id} className="inline-flex items-center gap-1">
                    <Avatar personne_id={r.personne_id} prenom={r.prenom}
                            avatar_version={r.avatar_version ?? 0} size="xs" />
                    <span className="text-[11px] font-semibold text-ocean-800">{r.prenom}</span>
                  </span>
                ))
              ) : mixte ? (
                <span className="text-[11px] font-semibold text-ocean-600 italic">
                  Attribution différente midi / soir
                </span>
              ) : (
                <span className="text-[11px] font-semibold text-ocean-500 italic">
                  Attribuer la journée…
                </span>
              )}
              <span className="text-[10px] text-ocean-400">{showJourPicker ? '▾' : '▸'}</span>
              {setRespJour.isPending && (
                <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-coral-500 border-t-transparent animate-spin" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowPrefs(v => !v)}
            className="text-[10px] font-semibold text-ocean-600 hover:text-coral-600 transition mt-0.5"
            aria-expanded={showPrefs}
          >
            ⚙ Préférences du jour
            {jour.prefs_jour.length > 0 && (
              <span className="ml-1 px-1 rounded bg-ocean-100 text-ocean-800">{jour.prefs_jour.length}</span>
            )}
            {' '}{showPrefs ? '▾' : '▸'}
          </button>
        </div>
        {jour.meteo ? (
          <div
            className="shrink-0 inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-ocean-50 border border-ocean-100 text-xs"
            title={`${jour.meteo.label} — précipitations ${jour.meteo.precip} mm`}
          >
            <span className="text-base leading-none" aria-hidden>{jour.meteo.emoji}</span>
            <span className="font-bold text-ocean-900">{Math.round(jour.meteo.tmax)}°</span>
            <span className="text-ocean-500">/ {Math.round(jour.meteo.tmin)}°</span>
          </div>
        ) : (
          <span className="shrink-0 text-[10px] text-ocean-400 italic pt-1">météo à venir</span>
        )}
      </div>

      {/* Responsables de la JOURNÉE entière — remplace midi ET soir */}
      {showJourPicker && (
        <div className="mb-3 p-2 rounded-lg bg-ocean-50/50 border border-ocean-100 anim-in">
          <p className="text-[10px] text-ocean-600 mb-1.5 leading-snug">
            La sélection s'applique au <strong>midi ET au soir</strong> du {fmtDate(jour.date)} —
            une, deux ou plus de personnes ; au moins une doit rester responsable.
            {mixte && (
              <span className="block text-coral-700 font-semibold mt-0.5">
                ⚠️ Midi et soir ont des attributions différentes : la sélection les remplacera toutes les deux.
              </span>
            )}
            {setRespJour.isPending && <span className="text-coral-600 font-semibold"> Enregistrement…</span>}
          </p>
          <div className="flex flex-wrap gap-1">
            {personnesActives.map(p => {
              const actif = jourIds.includes(p.id)
              const dernier = actif && jourIds.length <= 1
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={setRespJour.isPending || dernier}
                  onClick={() => toggleJour(p.id)}
                  aria-pressed={actif}
                  title={dernier ? 'Au moins une personne doit rester responsable de la journée' : undefined}
                  className={`inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full border text-[11px] font-semibold transition disabled:opacity-60 ${
                    actif
                      ? 'bg-coral-500 border-coral-500 text-white shadow-sm'
                      : 'bg-white border-ocean-200 text-ocean-700 hover:border-coral-400 hover:text-coral-700'
                  }`}
                >
                  <Avatar personne_id={p.id} prenom={p.prenom}
                          avatar_version={p.avatar_version} size="xs" />
                  {actif ? '✓ ' : ''}{p.prenom}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Préférences spécifiques à CE jour (union avec celles du séjour) */}
      {showPrefs && (
        <div className="mb-3 p-2 rounded-lg bg-ocean-50/50 border border-ocean-100 anim-in">
          <p className="text-[10px] text-ocean-600 mb-1.5">
            S'ajoutent aux préférences du séjour, uniquement pour le {fmtDate(jour.date)}.
            {setPrefsJour.isPending && <span className="text-coral-600 font-semibold"> Enregistrement…</span>}
          </p>
          <PrefsChips
            disponibles={prefsDisponibles}
            actives={jour.prefs_jour}
            onToggle={togglePrefJour}
            disabled={setPrefsJour.isPending}
          />
        </div>
      )}

      {!hasConvives ? (
        <p className="text-sm text-ocean-500 italic py-2">
          Personne d'inscrit aux repas ce jour-là pour l'instant —
          les coches de l'onglet 🍽️ Repas alimentent les suggestions.
        </p>
      ) : (
        <div className="space-y-3">
          {jour.repas.M && (
            <RepasBlock moment="M" data={jour.repas.M} catalogue={catalogue}
                        menusPersos={menusPersos} prefsDisponibles={prefsDisponibles} />
          )}
          {jour.repas.S && (
            <RepasBlock moment="S" data={jour.repas.S} catalogue={catalogue}
                        menusPersos={menusPersos} prefsDisponibles={prefsDisponibles} />
          )}
        </div>
      )}
    </div>
  )
}

function RepasBlock({ moment, data, catalogue, menusPersos, prefsDisponibles }: {
  moment: 'M' | 'S'
  data: MenuRepasJour
  catalogue: MenuCatalogueItem[]
  menusPersos: MenuPersoOut[]
  prefsDisponibles: PrefDisponible[]
}) {
  const [showAlt, setShowAlt] = useState(false)
  const [showIngredients, setShowIngredients] = useState(false)
  const [showRecette, setShowRecette] = useState(false)
  const [showChanger, setShowChanger] = useState(false)
  const [showPrefsRepas, setShowPrefsRepas] = useState(false)
  const [showRespPicker, setShowRespPicker] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const selection = useSetMenuSelection()
  const echange = useEchangerMenus()
  const setPrefsRepas = useSetPrefsRepas()
  const setResponsables = useSetResponsables()
  const personnesQ = usePersonnes()
  const nbTotal = data.nb_adulte_ado + data.nb_enfant
  const titre = moment === 'M' ? '☀️ Midi' : '🌙 Soir'
  const prefsRepas = data.prefs_repas ?? []
  const prefsHeritees = data.prefs_heritees ?? []
  const responsables = data.responsables ?? []
  const personnesActives = (personnesQ.data ?? [])
    .filter(p => p.actif)
    .sort((a, b) => a.ordre - b.ordre || a.prenom.localeCompare(b.prenom))

  const togglePrefRepas = (key: string) => {
    const next = prefsRepas.includes(key)
      ? prefsRepas.filter(k => k !== key)
      : [...prefsRepas, key]
    setPrefsRepas.mutate({ repas_id: data.repas_id, prefs: next })
  }

  const toggleResponsable = (pid: number) => {
    const ids = responsables.map(r => r.personne_id)
    // Une fois le repas attribué, au moins UNE personne doit rester responsable
    if (ids.includes(pid) && ids.length <= 1) return
    const next = ids.includes(pid) ? ids.filter(i => i !== pid) : [...ids, pid]
    setResponsables.mutate({ repas_id: data.repas_id, personne_ids: next })
  }

  if (nbTotal <= 0) {
    return (
      <div className="rounded-lg border border-ocean-100 bg-ocean-50/40 px-3 py-2">
        <div className="text-[11px] uppercase tracking-wide font-bold text-ocean-700">{titre}</div>
        <p className="text-xs text-ocean-500 italic mt-0.5">Aucun convive inscrit.</p>
      </div>
    )
  }

  const s = data.suggestion

  // === Drag & drop : échange du menu avec un autre repas =====================
  const onDragStart = (e: React.DragEvent) => {
    if (!s) return
    e.dataTransfer.setData(
      'application/x-hossegor-menu',
      JSON.stringify({ repas_id: data.repas_id, menu_id: s.id }),
    )
    e.dataTransfer.effectAllowed = 'move'
  }
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!s) return
    try {
      const src = JSON.parse(e.dataTransfer.getData('application/x-hossegor-menu'))
      if (!src?.repas_id || src.repas_id === data.repas_id) return
      echange.mutate({
        repas_id_a: src.repas_id, menu_id_a: src.menu_id,
        repas_id_b: data.repas_id, menu_id_b: s.id,
      })
    } catch { /* drop d'autre chose : ignorer */ }
  }

  const busy = selection.isPending || echange.isPending

  return (
    <div
      onDragOver={e => {
        if (e.dataTransfer.types.includes('application/x-hossegor-menu')) {
          e.preventDefault()
          setDragOver(true)
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`rounded-lg border px-3 py-2.5 transition ${
        dragOver
          ? 'border-coral-400 bg-coral-50/60 ring-2 ring-coral-300'
          : 'border-ocean-100 bg-white'
      }`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-[11px] uppercase tracking-wide font-bold text-ocean-700 flex items-center gap-1.5">
          {titre}
          <button
            type="button"
            onClick={() => setShowPrefsRepas(v => !v)}
            className="normal-case tracking-normal font-semibold text-[10px] text-ocean-500 hover:text-coral-600 transition"
            aria-expanded={showPrefsRepas}
            title="Préférences propres à ce repas (s'ajoutent au paramétrage général)"
          >
            ⚙{prefsRepas.length > 0 && <span className="ml-0.5 px-1 rounded bg-ocean-100 text-ocean-800">{prefsRepas.length}</span>}
          </button>
        </span>
        <span className="text-[11px] text-ocean-700 font-semibold whitespace-nowrap">
          🧑 {data.nb_adulte_ado}
          {data.nb_enfant > 0 && <> · 🧒 {data.nb_enfant}</>}
          {data.nb_bebe > 0 && <> · 👶 {data.nb_bebe}</>}
        </span>
      </div>

      {/* Préférences PROPRES À CE REPAS — héritent du général, ajustables ici */}
      {showPrefsRepas && (
        <div className="mb-2 p-2 rounded-lg bg-ocean-50/60 border border-ocean-100 anim-in">
          <p className="text-[10px] text-ocean-600 mb-1 leading-snug">
            Par défaut ce repas reprend le paramétrage général
            {prefsHeritees.length > 0 && (
              <> (hérité : {prefsHeritees.map(k => {
                const p = prefsDisponibles.find(x => x.key === k)
                return p ? `${p.icon} ${p.label}` : k
              }).join(' · ')})</>
            )}.
            Ajoute ici des contraintes <strong>pour ce repas uniquement</strong> — p. ex. un invité
            intolérant au lactose présent à ce seul repas.
            {setPrefsRepas.isPending && <span className="text-coral-600 font-semibold"> Enregistrement…</span>}
          </p>
          <PrefsChips
            disponibles={prefsDisponibles}
            actives={prefsRepas}
            onToggle={togglePrefRepas}
            disabled={setPrefsRepas.isPending}
          />
        </div>
      )}

      {/* Responsable(s) du repas — attribution incrémentale à 1..n personnes */}
      <div className="mb-1.5">
        <button
          type="button"
          onClick={() => setShowRespPicker(v => !v)}
          className="text-[11px] font-semibold text-ocean-700 hover:text-coral-600 transition text-left"
          aria-expanded={showRespPicker}
          title="Qui gère ce repas ? Clique pour attribuer (plusieurs personnes possibles)"
        >
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <span aria-hidden>👨‍🍳</span>
            {responsables.length > 0 ? (
              responsables.map(r => (
                <span key={r.personne_id} className="inline-flex items-center gap-1">
                  <Avatar personne_id={r.personne_id} prenom={r.prenom}
                          avatar_version={r.avatar_version ?? 0} size="xs" />
                  <span className="text-ocean-900">{r.prenom}</span>
                </span>
              ))
            ) : (
              <span className="italic text-ocean-500">Attribuer ce repas…</span>
            )}
            <span className="text-ocean-400">{showRespPicker ? '▾' : '▸'}</span>
            {setResponsables.isPending && (
              <span className="inline-block w-2.5 h-2.5 rounded-full border-2 border-coral-500 border-t-transparent animate-spin" />
            )}
          </span>
        </button>
        {showRespPicker && (
          <div className="mt-1 p-2 rounded-lg bg-ocean-50/60 border border-ocean-100 anim-in">
            <p className="text-[10px] text-ocean-600 mb-1 leading-snug">
              Attribue ce repas à une, deux ou plus de personnes — re-clique pour retirer.
              Une fois attribué, au moins une personne doit rester responsable.
            </p>
            <div className="flex flex-wrap gap-1">
              {personnesActives.map(p => {
                const actif = responsables.some(r => r.personne_id === p.id)
                const dernier = actif && responsables.length <= 1
                return (
                  <button
                    key={p.id}
                    type="button"
                    disabled={setResponsables.isPending || dernier}
                    onClick={() => toggleResponsable(p.id)}
                    aria-pressed={actif}
                    title={dernier ? 'Au moins une personne doit rester responsable de ce repas' : undefined}
                    className={`inline-flex items-center gap-1 pl-1 pr-2 py-0.5 rounded-full border text-[11px] font-semibold transition disabled:opacity-60 ${
                      actif
                        ? 'bg-coral-500 border-coral-500 text-white shadow-sm'
                        : 'bg-white border-ocean-200 text-ocean-700 hover:border-coral-400 hover:text-coral-700'
                    }`}
                  >
                    <Avatar personne_id={p.id} prenom={p.prenom}
                            avatar_version={p.avatar_version} size="xs" />
                    {actif ? '✓ ' : ''}{p.prenom}
                  </button>
                )
              })}
              {personnesActives.length === 0 && (
                <span className="text-[11px] text-ocean-500 italic">Aucune personne active.</span>
              )}
            </div>
          </div>
        )}
      </div>

      {data.aucun_compatible && (
        <p className="text-xs text-coral-700 font-semibold">
          Aucune recette du catalogue ne satisfait toutes les préférences ce jour-là —
          allège les préférences du jour ou du séjour.
        </p>
      )}

      {s && (
        <>
          {/* Carte menu draggable */}
          <div
            draggable={!busy}
            onDragStart={onDragStart}
            className={`-mx-1 px-1 rounded-md ${busy ? '' : 'cursor-grab active:cursor-grabbing'}`}
            title="Glisser-déposer sur un autre repas pour échanger les menus"
          >
            <div className="flex items-start gap-1.5">
              <span aria-hidden className="text-ocean-300 text-xs pt-0.5 select-none">⠿</span>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-ocean-900 leading-tight">
                  {s.nom}
                  {s.fige && (
                    <span className="ml-1.5 align-middle px-1.5 py-0.5 rounded bg-coral-100 border border-coral-300 text-coral-800 text-[9px] font-bold uppercase tracking-wide">
                      📌 choisi
                    </span>
                  )}
                </div>
                <p className="text-xs text-ocean-700 mt-0.5 leading-snug">{s.description}</p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            {s.dans_budget && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-300 text-emerald-800 text-[10px] font-bold">
                🎯 Dans le budget
              </span>
            )}
            {s.kid_friendly && data.nb_enfant > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold">
                🧒 Adapté aux enfants
              </span>
            )}
            {s.regimes?.vegan && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold">🌱 Végan</span>
            )}
            {!s.regimes?.vegan && s.regimes?.vege && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 text-[10px] font-semibold">🥬 Végé (option)</span>
            )}
            {s.tags.map(t => TAG_LABEL[t] && (
              <span key={t} className="px-1.5 py-0.5 rounded bg-sand-50 border border-sand-200 text-sand-800 text-[10px] font-semibold">
                {TAG_LABEL[t]}
              </span>
            ))}
            <span className="ml-auto text-[11px] font-bold text-coral-700 whitespace-nowrap">
              ≈ {fmtEur(s.prix_total_estime)}
              <span className="text-ocean-500 font-normal"> ({fmtEur(s.prix_par_adulte)}/adulte)</span>
            </span>
          </div>

          {/* Actions : ingrédients / changer / défiger */}
          <div className="flex flex-wrap items-center gap-2 mt-2 pt-1.5 border-t border-ocean-50">
            {(s.ingredients?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowIngredients(v => !v)}
                className="text-[11px] font-semibold text-ocean-600 hover:text-coral-600 transition"
                aria-expanded={showIngredients}
              >
                {showIngredients ? '▾' : '▸'} 🧾 Ingrédients pour {nbTotal} pers.
              </button>
            )}
            {(s.recette?.length ?? 0) > 0 && (
              <button
                type="button"
                onClick={() => setShowRecette(v => !v)}
                className="text-[11px] font-semibold text-ocean-600 hover:text-coral-600 transition"
                aria-expanded={showRecette}
              >
                {showRecette ? '▾' : '▸'} 📖 Recette
              </button>
            )}
            <button
              type="button"
              disabled={busy}
              onClick={() => setShowChanger(true)}
              className="text-[11px] font-semibold text-ocean-600 hover:text-coral-600 transition disabled:opacity-50"
            >
              ✏️ Changer le menu
            </button>
            {s.fige && (
              <button
                type="button"
                disabled={busy}
                onClick={() => selection.mutate({ repas_id: data.repas_id, menu_id: null })}
                className="text-[11px] font-semibold text-ocean-600 hover:text-coral-600 transition disabled:opacity-50"
              >
                ↺ Revenir à la suggestion auto
              </button>
            )}
            {busy && (
              <span className="inline-block w-3 h-3 rounded-full border-2 border-coral-500 border-t-transparent animate-spin" />
            )}
          </div>

          {/* Ingrédients & quantités calculés pour le groupe */}
          {showIngredients && (s.ingredients?.length ?? 0) > 0 && (
            <div className="mt-2 rounded-lg bg-sand-50/70 border border-sand-200 px-3 py-2 anim-in">
              <div className="text-[10px] uppercase tracking-wide font-bold text-sand-800 mb-1">
                Liste de courses — {data.nb_adulte_ado} adulte{data.nb_adulte_ado > 1 ? 's' : ''}
                {data.nb_enfant > 0 && <> + {data.nb_enfant} enfant{data.nb_enfant > 1 ? 's' : ''} (60 % d'une portion)</>}
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-sand-900">
                {s.ingredients!.map((ing, i) => (
                  <li key={i} className="flex justify-between gap-2 border-b border-sand-100 last:border-0 py-0.5">
                    <span>{ing.nom}</span>
                    <span className="font-semibold whitespace-nowrap">{ing.quantite} {ing.unite}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recette complète, étape par étape */}
          {showRecette && (s.recette?.length ?? 0) > 0 && (
            <div className="mt-2 rounded-lg bg-ocean-50/60 border border-ocean-100 px-3 py-2 anim-in">
              <div className="text-[10px] uppercase tracking-wide font-bold text-ocean-800 mb-1">
                📖 Recette — {s.nom}
              </div>
              <ol className="space-y-1 text-xs text-ocean-800 list-none">
                {s.recette!.map((etape, i) => (
                  <li key={i} className="flex gap-2 leading-snug">
                    <span className="shrink-0 w-4 h-4 rounded-full bg-ocean-600 text-white text-[9px] font-bold flex items-center justify-center mt-0.5">
                      {i + 1}
                    </span>
                    <span>{etape}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {data.alternatives.length > 0 && (
            <div className="mt-2 pt-1.5 border-t border-ocean-50">
              <button
                type="button"
                onClick={() => setShowAlt(v => !v)}
                className="text-[11px] font-semibold text-ocean-600 hover:text-coral-600 transition"
                aria-expanded={showAlt}
              >
                {showAlt ? '▾' : '▸'} Autres idées ({data.alternatives.length})
              </button>
              {showAlt && (
                <ul className="mt-1 space-y-1 anim-in">
                  {data.alternatives.map(a => (
                    <li key={a.id} className="text-xs text-ocean-700 leading-snug flex items-start justify-between gap-2">
                      <span>
                        <span className="font-semibold text-ocean-900">{a.nom}</span>
                        {' '}· ≈ {fmtEur(a.prix_total_estime)}
                        <span className="block text-[10px] text-ocean-500">{a.description}</span>
                      </span>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => selection.mutate({ repas_id: data.repas_id, menu_id: a.id })}
                        className="shrink-0 px-1.5 py-0.5 rounded border border-ocean-200 text-[10px] font-semibold text-ocean-700 hover:border-coral-400 hover:text-coral-700 transition disabled:opacity-50"
                      >
                        Choisir
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      {/* Modale : choisir un menu du catalogue ou créer le sien */}
      {showChanger && (
        <ChangerMenuModal
          moment={moment}
          catalogue={catalogue}
          menusPersos={menusPersos}
          repasId={data.repas_id}
          currentId={s?.id ?? null}
          onPick={(menuId) => {
            selection.mutate({ repas_id: data.repas_id, menu_id: menuId })
            setShowChanger(false)
          }}
          onCancel={() => setShowChanger(false)}
        />
      )}
    </div>
  )
}

/* ============================================================================
   Modale « Changer le menu » — tout le catalogue du moment (midi/soir)
   ========================================================================= */
function ChangerMenuModal({ moment, catalogue, menusPersos, repasId, currentId, onPick, onCancel }: {
  moment: 'M' | 'S'
  catalogue: MenuCatalogueItem[]
  menusPersos: MenuPersoOut[]
  repasId: number
  currentId: string | null
  onPick: (menuId: string) => void
  onCancel: () => void
}) {
  const items = catalogue.filter(c => c.moment.includes(moment))
  const persos = menusPersos.filter(p => (p.moment || 'MS').includes(moment))
  const deletePerso = useDeleteMenuPerso()
  const [showCreer, setShowCreer] = useState(false)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 bg-ocean-900/50 backdrop-blur-sm anim-in"
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
    >
      {/* Structure : en-tête FIXE + corps scrollable à barre fine → la liste
          est toujours entièrement accessible, le titre et la croix restent visibles. */}
      <div
        className="card relative max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden p-0"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-ocean-100 shrink-0 bg-ocean-50/60">
          <h3 className="font-bold text-ocean-900">
            Choisir un menu — {moment === 'M' ? '☀️ Midi' : '🌙 Soir'}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Fermer"
            className="shrink-0 w-8 h-8 rounded-full text-ocean-500 hover:text-coral-600 hover:bg-coral-50 flex items-center justify-center font-bold transition"
          >
            ✕
          </button>
        </div>

        <div className="overflow-y-auto scroll-slim px-4 py-3">

        {/* === Créer SON PROPRE menu ======================================= */}
        <div className="mb-3 rounded-xl border-2 border-coral-200 bg-coral-50/40 p-3">
          <button
            type="button"
            onClick={() => setShowCreer(v => !v)}
            className="w-full flex items-center justify-between font-bold text-coral-800 text-sm"
            aria-expanded={showCreer}
          >
            <span>✍️ Indiquer mon propre menu</span>
            <span>{showCreer ? '▾' : '▸'}</span>
          </button>
          {showCreer && (
            <CreerMenuPersoForm
              moment={moment}
              repasId={repasId}
              onDone={onCancel}
            />
          )}
        </div>

        {/* === Mes menus déjà créés ======================================== */}
        {persos.length > 0 && (
          <div className="mb-3">
            <h4 className="text-[11px] uppercase tracking-wide font-bold text-ocean-700 mb-1.5">
              ✍️ Mes menus
            </h4>
            <div className="space-y-1.5">
              {persos.map(p => (
                <div
                  key={p.id}
                  className={`rounded-lg border-2 px-3 py-2 flex items-start gap-2 ${
                    p.id === currentId ? 'border-coral-400 bg-coral-50/60' : 'border-sand-200 bg-sand-50/50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => onPick(p.id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="font-bold text-ocean-900 text-sm">
                        {p.nom}
                        {p.id === currentId && <span className="ml-1.5 text-[9px] text-coral-700 font-bold uppercase">actuel</span>}
                      </span>
                      {p.prix_par_adulte > 0 && (
                        <span className="text-[11px] font-bold text-coral-700 shrink-0">{fmtEur(p.prix_par_adulte)}/ad.</span>
                      )}
                    </div>
                    {p.description && (
                      <div className="text-[11px] text-ocean-600 leading-snug mt-0.5">{p.description}</div>
                    )}
                    <div className="text-[10px] text-ocean-500 mt-0.5">
                      {p.ingredients_par_adulte.length} ingrédient{p.ingredients_par_adulte.length > 1 ? 's' : ''} (quantités auto-ajustées aux convives)
                    </div>
                  </button>
                  <button
                    type="button"
                    disabled={deletePerso.isPending}
                    onClick={() => {
                      if (window.confirm(`Supprimer le menu « ${p.nom} » ? Les repas qui l'utilisent reviendront en suggestion automatique.`)) {
                        deletePerso.mutate(Number(p.id.replace('perso-', '')))
                      }
                    }}
                    aria-label={`Supprimer ${p.nom}`}
                    title="Supprimer ce menu"
                    className="shrink-0 w-6 h-6 rounded-md text-coral-600 hover:text-white hover:bg-coral-500 transition disabled:opacity-50 flex items-center justify-center text-xs font-bold"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <h4 className="text-[11px] uppercase tracking-wide font-bold text-ocean-700 mb-1.5">
          📚 Catalogue ({items.length} menus)
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pb-2">
          {items.map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => onPick(c.id)}
              className={`w-full text-left rounded-lg border-2 px-3 py-2 transition ${
                c.id === currentId
                  ? 'border-coral-400 bg-coral-50/60'
                  : 'border-ocean-100 bg-white hover:border-ocean-300'
              }`}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="font-bold text-ocean-900 text-sm">
                  {c.nom}
                  {c.id === currentId && <span className="ml-1.5 text-[9px] text-coral-700 font-bold uppercase">actuel</span>}
                </span>
                <span className="text-[11px] font-bold text-coral-700 shrink-0">{fmtEur(c.prix_par_adulte)}/ad.</span>
              </div>
              <div className="text-[11px] text-ocean-600 leading-snug mt-0.5">{c.description}</div>
              <div className="flex flex-wrap gap-1 mt-1">
                {c.regimes.vegan && <span className="px-1 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[9px] font-semibold">🌱 végan</span>}
                {!c.regimes.vegan && c.regimes.vege && <span className="px-1 py-0.5 rounded bg-emerald-50 text-emerald-800 text-[9px] font-semibold">🥬 végé</span>}
                {c.regimes.sans_gluten && <span className="px-1 py-0.5 rounded bg-sand-50 text-sand-800 text-[9px] font-semibold">sans gluten</span>}
                {c.regimes.sans_lactose && <span className="px-1 py-0.5 rounded bg-sand-50 text-sand-800 text-[9px] font-semibold">sans lactose</span>}
                {c.regimes.fruits_mer && <span className="px-1 py-0.5 rounded bg-coral-50 text-coral-700 text-[9px] font-semibold">🦐 fruits de mer</span>}
                {c.regimes.alcool && <span className="px-1 py-0.5 rounded bg-coral-50 text-coral-700 text-[9px] font-semibold">🍷 alcool</span>}
              </div>
            </button>
          ))}
        </div>
        </div>{/* fin corps scrollable */}
      </div>
    </div>
  )
}
